import React, { useMemo, useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ImageStrip from './ImageStrip';
import { supabaseHelpers } from '../../../lib/supabase';

// Card totalmente novo: hierarquia centrada em marca, nome, tipo, cor, quantidade e preço/g
const FilamentCard = ({ filament, onEdit, onViewHistory, onMarkDepleted }) => {
  const [usageCount, setUsageCount] = useState(() => {
    const pre = filament?.usageCount ?? filament?.usage_count;
    return Number.isFinite(pre) ? Number(pre) : 0;
  });
  const [usageLoading, setUsageLoading] = useState(false);
  const [lastUsageDate, setLastUsageDate] = useState(null);
  const [projectImageUrls, setProjectImageUrls] = useState([]);

  const brand = filament?.brand || 'Marca';
  const type = filament?.material || 'Tipo';
  const filamentName = filament?.name || filament?.productName || `${type}`;
  const diameter = filament?.diameter ? `${filament?.diameter}mm` : null;
  const colorName = filament?.colorName || 'Cor';
  const original = Number(filament?.originalWeight || 0);
  const current = Number(filament?.currentWeight || 0);
  const costPerGram = Number(filament?.costPerGram || 0);
  const pct = original > 0 ? Math.max(0, Math.min(100, (current / original) * 100)) : 0;
  const availableValue = (() => {
    const total = Number(filament?.totalCost || 0);
    if (total > 0 && original > 0) return total * (current / original);
    if (costPerGram > 0 && current > 0) return costPerGram * current;
    return 0;
  })();
  const rollPrice = (() => {
    const total = Number(filament?.totalCost || 0);
    if (total > 0) return total;
    if (costPerGram > 0 && original > 0) return costPerGram * original;
    return 0;
  })();

  const swatchColor = filament?.colorHex || filament?.color_hex || '#A0A0A0';
  // Borda dupla para cores claras
  const hexToRgb = (hex) => {
    const clean = (hex || '').replace('#', '');
    const bigint = parseInt(clean.length === 3 ? clean.split('').map(c=>c+c).join('') : clean, 16);
    const r = (bigint >> 16) & 255; const g = (bigint >> 8) & 255; const b = bigint & 255; return { r, g, b };
  };
  const { r, g, b } = hexToRgb(swatchColor);
  const luminance = (0.2126 * (r/255)) + (0.7152 * (g/255)) + (0.0722 * (b/255));
  const swatchBorderColor = luminance > 0.75 ? 'rgba(0,0,0,0.35)' : 'var(--color-background)';

  const stockLabel = pct <= 20 ? 'Estoque baixo' : pct <= 40 ? 'Estoque médio' : 'Estoque bom';

  const imageUrls = useMemo(() => {
    const base = Array.isArray(filament?.image_urls) ? filament?.image_urls : (filament?.image ? [filament?.image] : []);
    return Array.isArray(base) ? base.slice(0, 4) : [];
  }, [filament]);

  // Calcular metragem aproximada a partir de gramsPerMeter ou pela densidade/diâmetro
  const computeGramatura = (density, diameterMm) => {
    const d = parseFloat(density);
    const dia = parseFloat(diameterMm);
    if (!Number.isFinite(d) || !Number.isFinite(dia) || d <= 0 || dia <= 0) return undefined;
    const radiusCm = (dia / 10) / 2; // mm -> cm, raio
    const areaCm2 = Math.PI * Math.pow(radiusCm, 2);
    const volumeCm3PerMeter = areaCm2 * 100; // 100 cm em 1 m
    const gramsPerMeter = d * volumeCm3PerMeter;
    return gramsPerMeter;
  };

  const gramsPerMeter = (() => {
    const gpm = parseFloat(filament?.gramsPerMeter ?? filament?.grams_per_meter ?? '');
    if (Number.isFinite(gpm) && gpm > 0) return gpm;
    const density = filament?.density ?? 1.24;
    const dia = parseFloat(filament?.diameter || '1.75');
    const computed = computeGramatura(density, dia);
    return Number.isFinite(computed) ? parseFloat(computed.toFixed(3)) : undefined;
  })();

  const remainingMeters = (Number.isFinite(gramsPerMeter) && gramsPerMeter > 0) ? (current / gramsPerMeter) : undefined;
  const rollMeters = (Number.isFinite(gramsPerMeter) && gramsPerMeter > 0) ? (original / gramsPerMeter) : undefined;

  // Carregar contagem de uso (jobs/projetos) de forma leve
  useEffect(() => {
    let active = true;
    const fetchUsage = async () => {
      if (!filament?.id) return;
      // Evitar refetch se já houver valor precomputado
      if (usageCount && usageCount > 0) return;
      setUsageLoading(true);
      try {
        const { data, error } = await supabaseHelpers?.getFilamentUsageHistory?.(filament?.id);
        if (error) throw error;
        const count = Array.isArray(data) ? data.length : 0;
        const last = Array.isArray(data) && data[0]?.usage_date ? data[0]?.usage_date : null;
        if (active) {
          setUsageCount(count);
          setLastUsageDate(last);
          // Coletar imagens dos projetos recentes
          const imgs = [];
          (Array.isArray(data) ? data : [])?.slice(0, 10)?.forEach(rec => {
            const urls = rec?.project?.image_urls;
            if (Array.isArray(urls)) urls?.forEach(u => { if (typeof u === 'string') imgs.push(u); });
          });
          const unique = Array.from(new Set(imgs));
          setProjectImageUrls(unique?.slice(0, 8));
        }
      } catch (err) {
        // Silencioso: se falhar, mantemos 0
        if (active) {
          setUsageCount(prev => Number.isFinite(prev) ? prev : 0);
        }
      } finally {
        if (active) setUsageLoading(false);
      }
    };
    fetchUsage();
    return () => { active = false; };
  }, [filament?.id]);

  return (
    <section className="relative rounded-2xl bg-card border border-border shadow-none overflow-hidden transition-all hover:shadow-xs">
      {/* Swatch alinhado ao card agrupado */}
      <div
        className="absolute top-4 left-4 w-12 h-12 rounded-full ring-2 ring-border"
        style={{ backgroundColor: swatchColor, border: `2px solid ${swatchBorderColor}` }}
        aria-label={`Cor ${colorName}`}
        title={colorName}
      />

      {/* Badges à direita (marca) */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20" title={brand}>{brand}</span>
      </div>

      {/* Cabeçalho unificado com espaçamento maior do swatch e proteção à direita */}
      <div className="pt-5 pl-20 pr-20 pb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground" title={colorName}>{colorName}</span>
            </div>
            <h3 className="text-base font-medium text-foreground mt-1" title={type}>{type}</h3>
            {diameter && (
              <div className="text-sm text-muted-foreground" title={diameter}>{diameter}</div>
            )}
          </div>
        </div>
      </div>

      {/* Corpo: progresso e infos secundárias */}
      <div className="px-5 pb-5">
        {/* Progresso de estoque */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">Filamento restante</span>
            <span className="text-sm font-medium text-foreground">
              {current}g{remainingMeters !== undefined ? ` · ${remainingMeters.toFixed(1)} m` : ''}
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div
              className={`${pct <= 20 ? 'bg-error' : pct <= 40 ? 'bg-warning' : 'bg-success'} h-2 rounded-full transition-all`}
              style={{ width: `${Math.max(pct, 5)}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className={`${pct <= 20 ? 'text-error' : pct <= 40 ? 'text-warning' : 'text-success'} text-xs font-medium`}>{stockLabel}</span>
            <span className="text-xs text-muted-foreground">{pct.toFixed(1)}%</span>
          </div>
          {rollMeters !== undefined && (
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-muted-foreground">Rolo</span>
              <span className="text-xs text-foreground">{original}g · {rollMeters.toFixed(1)} m</span>
            </div>
          )}

          {/* Operação: usado em X jobs */}
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Activity" size={16} className="text-muted-foreground" />
                <span className="text-sm font-semibold text-foreground">Operação</span>
              </div>
              <span className="text-sm text-foreground">
                {usageLoading ? 'Carregando…' : `Usado em ${usageCount || 0} jobs`}
              </span>
            </div>
            {lastUsageDate && (
              <div className="mt-1 text-xs text-muted-foreground text-right">Último uso: {new Date(lastUsageDate)?.toLocaleDateString('pt-BR')}</div>
            )}
          </div>
          {/* Infos reorganizadas: Valor por gr, por kg, em estoque, data */}
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Financeiro</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Preço do rolo</span>
              <span className="text-primary text-lg font-bold">R$ {rollPrice.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Valor por gr</span>
              <span className="text-sm font-medium text-foreground">R$ {costPerGram.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Valor por kg</span>
              <span className="text-sm font-medium text-foreground">R$ {(costPerGram * 1000).toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Valor em estoque</span>
              <span className="text-sm font-medium text-foreground">R$ {availableValue.toFixed(2)}</span>
            </div>
          </div>

          <>
            <div className="h-px w-full bg-muted my-4" />
            <div className="mt-6 space-y-2">
              {filament?.purchaseDate && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Data da compra</span>
                  <span className="text-sm font-medium text-foreground">{filament?.purchaseDate}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Fornecedor</span>
                <span className="text-sm font-medium text-foreground truncate" title={filament?.supplier || '—'}>{filament?.supplier || '—'}</span>
              </div>
              {filament?.notes && (
                <div className="mt-1">
                  <span className="text-xs text-muted-foreground">Observações</span>
                  <div className="text-sm text-foreground mt-0.5 truncate" title={filament?.notes}>{filament?.notes}</div>
                </div>
              )}
            </div>
          </>

          {/* Imagens dos últimos projetos (fallback para imagens do rolo) */}
          <ImageStrip images={(projectImageUrls?.length ? projectImageUrls : imageUrls)} className="mt-4" />
        </div>

        {/* Removida a barra divisória inferior */}
      </div>

      {/* Rodapé: toolbar contínua, clean, com separadores sutis (menor) */}
      <div className="px-5 pb-5">
        <div className="flex rounded-lg overflow-hidden divide-x divide-border border border-border/70 bg-muted/30">
          <Button variant="ghost" size="xs" iconSize={16} className="flex-1 rounded-none" onClick={() => onEdit?.(filament)} iconName="Edit" iconPosition="left">Editar</Button>
          <Button variant="ghost" size="xs" iconSize={16} className="flex-1 rounded-none" onClick={() => onViewHistory?.(filament)} iconName="Projects" iconPosition="left">Histórico</Button>
          <Button variant="ghost" size="xs" iconSize={16} className="flex-1 rounded-none text-error" onClick={() => onMarkDepleted?.(filament)} iconName="Trash2" iconPosition="left">Excluir</Button>
        </div>
      </div>
    </section>
  );
};

export default FilamentCard;
